// Google Drive + Google Sheets API ラッパ。
// fetch 直叩き(google-spreadsheet 等のライブラリ依存なし、軽量・依存最小)
//
// 認証: useAuthStore の access_token を使用
// スコープ: drive.file(つばめカードが作成・選択したファイルのみアクセス可)

import {
  ACTIVE_SHEET_TITLE,
  COL_TAGS,
  SHEETS_HEADER,
  SHEET_TITLE,
} from "./constants";
import { parseTags, serializeTags } from "./tags";
import type { BusinessCard } from "./types";

// ============================================================
// 共通ヘルパー
// ============================================================

class GoogleApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "GoogleApiError";
  }
}

async function authedFetch(
  url: string,
  accessToken: string,
  init: RequestInit = {},
): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new GoogleApiError(
      res.status,
      `Google API ${res.status}: ${text || res.statusText}`,
    );
  }
  return res;
}

// ============================================================
// Drive: つばめカードシート検出
// ============================================================

export interface DetectedSheet {
  id: string;
  name: string;
  createdTime: string;
  modifiedTime: string;
}

/**
 * ユーザーの Drive 内から「つばめカード」スプレッドシートを検出。
 *
 * drive.file スコープのため、つばめが作成 or ユーザーが選択したファイルのみ可視。
 * iOS 版でシートが作られていれば自動で見つかる前提。
 */
export async function detectTsubameSheet(
  accessToken: string,
): Promise<DetectedSheet | null> {
  const params = new URLSearchParams({
    q: `name='${SHEET_TITLE}' and mimeType='application/vnd.google-apps.spreadsheet' and trashed=false`,
    fields: "files(id,name,createdTime,modifiedTime)",
    pageSize: "10",
    orderBy: "modifiedTime desc",
  });

  const res = await authedFetch(
    `https://www.googleapis.com/drive/v3/files?${params.toString()}`,
    accessToken,
  );
  const data = (await res.json()) as { files?: DetectedSheet[] };
  return data.files?.[0] ?? null;
}

// ============================================================
// Sheets: アクティブタブの全行を読み取り
// ============================================================

interface SheetValuesResponse {
  range: string;
  majorDimension: string;
  values?: string[][];
}

/**
 * アクティブタブの全行(2 行目以降)を取得して BusinessCard 配列に変換。
 * 1 行目はヘッダなのでスキップ。
 * Web 版は 23 列(W列タグ含む)を読みに行く。iOS 版が 22 列のシートを使っている場合、
 * W列は空文字になり tags=[] となるだけで安全。
 */
export async function fetchAllCards(
  spreadsheetId: string,
  accessToken: string,
): Promise<BusinessCard[]> {
  // A2:W までの全行(23列・タグ列込み)
  const range = `${ACTIVE_SHEET_TITLE}!A2:W`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
    spreadsheetId,
  )}/values/${encodeURIComponent(range)}?valueRenderOption=UNFORMATTED_VALUE`;

  const res = await authedFetch(url, accessToken);
  const data = (await res.json()) as SheetValuesResponse;
  const rows = data.values ?? [];
  return rows.map((row, i) => ({ ...rowToCard(row), rowIndex: i + 2 }));
}

/**
 * シートのヘッダ行を確認し、「タグ」列(W列)が無ければ追加する。
 * iOS 版で作られたシートは 22 列までしかないので、Web 版から初回読込時に呼ぶ想定。
 * 既に 23 列存在する場合は何もしない(冪等)。
 */
export async function ensureTagsColumn(
  spreadsheetId: string,
  accessToken: string,
): Promise<void> {
  const range = `${ACTIVE_SHEET_TITLE}!A1:W1`;
  const getUrl = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
    spreadsheetId,
  )}/values/${encodeURIComponent(range)}`;
  const res = await authedFetch(getUrl, accessToken);
  const data = (await res.json()) as SheetValuesResponse;
  const headerRow = data.values?.[0] ?? [];

  const expected = SHEETS_HEADER[COL_TAGS]; // "タグ"
  if (headerRow[COL_TAGS]?.trim() === expected) {
    return; // 既に存在
  }

  // W1 セルに「タグ」を書き込む(USER_ENTERED で文字列として)
  const writeRange = `${ACTIVE_SHEET_TITLE}!W1`;
  const writeUrl = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
    spreadsheetId,
  )}/values/${encodeURIComponent(writeRange)}?valueInputOption=USER_ENTERED`;

  await authedFetch(writeUrl, accessToken, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      range: writeRange,
      majorDimension: "ROWS",
      values: [[expected]],
    }),
  });
}

/**
 * BusinessCard 配列をアクティブタブの末尾に追加する。
 * Sheets API の `values:append` を使用、表記不明な列は空セル。
 *
 * No(A列)は空にして Sheets 側で連番を採らないが、
 * 将来 メタタブ統合時に「最終番号」を見て採番するように拡張予定。
 */
export async function appendCards(
  spreadsheetId: string,
  cards: BusinessCard[],
  accessToken: string,
): Promise<{ appended: number }> {
  if (cards.length === 0) return { appended: 0 };

  const values = cards.map(cardToRow);
  const range = `${ACTIVE_SHEET_TITLE}!A:W`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
    spreadsheetId,
  )}/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`;

  const res = await authedFetch(url, accessToken, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      range,
      majorDimension: "ROWS",
      values,
    }),
  });
  await res.json();
  return { appended: cards.length };
}

/** BusinessCard を SHEETS_HEADER 順の string[] に変換 */
function cardToRow(c: BusinessCard): string[] {
  return [
    c.no ?? "",
    c.acquiredAt ?? "",
    c.name ?? "",
    c.company ?? "",
    c.department ?? "",
    c.title ?? "",
    c.postalCode ?? "",
    c.address ?? "",
    c.phone ?? "",
    c.mobile ?? "",
    c.fax ?? "",
    c.email1 ?? "",
    c.email2 ?? "",
    c.url ?? "",
    c.memo ?? "",
    c.imageUrl ?? "",
    c.imageId ?? "",
    c.nameEn ?? "",
    c.companyEn ?? "",
    c.departmentEn ?? "",
    c.titleEn ?? "",
    c.addressEn ?? "",
    serializeTags(c.tags ?? []),
  ];
}

/**
 * 指定行のタグを更新する(W列のみ書き込み)。
 * rowIndex は 1 始まり(ヘッダが 1 行目、データは 2 行目から)。
 */
export async function updateCardTags(
  spreadsheetId: string,
  rowIndex: number,
  tags: string[],
  accessToken: string,
): Promise<void> {
  const range = `${ACTIVE_SHEET_TITLE}!W${rowIndex}`;
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${encodeURIComponent(
    spreadsheetId,
  )}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`;

  await authedFetch(url, accessToken, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      range,
      majorDimension: "ROWS",
      values: [[serializeTags(tags)]],
    }),
  });
}

/**
 * Sheets の 1 行(string[])を BusinessCard に変換。
 * 列順は SHEETS_HEADER と一致前提(iOS 版と同じ)。
 */
function rowToCard(row: string[]): BusinessCard {
  // SHEETS_HEADER の順序:
  // No, 取得日, 氏名, 会社, 部署, 役職, 郵便番号, 住所,
  // 電話, 携帯, FAX, メール1, メール2, URL, メモ,
  // 画像, 画像ID, 氏名_英, 会社_英, 部署_英, 役職_英, 住所_英
  const get = (i: number): string => (row[i] ?? "").toString().trim();

  return {
    no: get(0),
    acquiredAt: get(1),
    name: get(2),
    company: get(3),
    department: get(4),
    title: get(5),
    postalCode: get(6),
    address: get(7),
    phone: get(8),
    mobile: get(9),
    fax: get(10),
    email1: get(11),
    email2: get(12),
    url: get(13),
    memo: get(14),
    imageUrl: get(15),
    imageId: get(16),
    nameEn: get(17),
    companyEn: get(18),
    departmentEn: get(19),
    titleEn: get(20),
    addressEn: get(21),
    tags: parseTags(get(22)),
  };
}

// ============================================================
// 検索・絞り込み(クライアントサイド)
// ============================================================

/**
 * クエリ文字列で名刺を絞り込み。
 * 氏名・会社・メール1/2・部署・役職・電話・携帯 を部分一致で検索。
 * 大文字小文字無視。空クエリは全件返す。
 */
export function filterCards(
  cards: BusinessCard[],
  query: string,
): BusinessCard[] {
  const q = query.trim().toLowerCase();
  if (!q) return cards;

  return cards.filter((c) => {
    const haystack = [
      c.name,
      c.company,
      c.department,
      c.title,
      c.email1,
      c.email2,
      c.phone,
      c.mobile,
      c.nameEn,
      c.companyEn,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

// ============================================================
// 列定義(UI 表示用)
// ============================================================

/** UI のテーブル列(優先度順)。
 * `width` は最低幅(min-width)。値が空でも縮まないようにする。
 * 値が長い場合は内容に応じて広がる(table 全体は overflow-x-auto)。
 */
export const VISIBLE_COLUMNS: Array<{
  key: keyof BusinessCard;
  label: string;
  width: string;
}> = [
  { key: "no", label: "No", width: "min-w-[3rem]" },
  { key: "name", label: "氏名", width: "min-w-[8rem]" },
  { key: "company", label: "会社", width: "min-w-[10rem]" },
  { key: "department", label: "部署", width: "min-w-[6rem]" },
  { key: "title", label: "役職", width: "min-w-[5rem]" },
  { key: "phone", label: "電話", width: "min-w-[8rem]" },
  { key: "mobile", label: "携帯", width: "min-w-[8rem]" },
  { key: "email1", label: "メール", width: "min-w-[12rem]" },
  { key: "acquiredAt", label: "取得日", width: "min-w-[6rem]" },
];

export { GoogleApiError };

// ヘッダ整合性チェック(将来の SchemaValidator 流用用 / Phase 1 では未使用)
export function validateHeaderRow(row: string[]): boolean {
  if (row.length < SHEETS_HEADER.length) return false;
  for (let i = 0; i < SHEETS_HEADER.length; i++) {
    if (row[i]?.trim() !== SHEETS_HEADER[i]) return false;
  }
  return true;
}
